import React, { useState, useEffect, useCallback } from 'react';
import { useApi } from '../../hooks/useApi';
import { isStandardApiResponse } from '../../types/api';

interface VPSStats {
  vps_info: {
    total_storage_gb: number;
    capacity_gb: number;
    usage_percentage: number;
    scale_recommendation: string;
    recommended_capacity: number;
  };
  user_stats: {
    total_users: number;
    active_users: number;
    inactive_users: number;
    plan_usage?: Record<string, number>;
  };
  service_breakdown: Array<{
    service__display_name: string;
    storage_gb: number;
    user_count: number;
  }>;
  trends: {
    storage_growth_30d: string;
    last_updated: string;
  };
}

/**
 * VPS統計カード
 * 管理者ポータルでVPS使用状況とスケール推奨を表示
 */
const VPSStatsCard: React.FC = () => {
  const { execute: apiCall } = useApi();
  const [vpsStats, setVpsStats] = useState<VPSStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchVPSStats = useCallback(async () => {
    try {
      setLoading(true);
      setError(null); // エラーをリセット
      const token = localStorage.getItem('authToken');
      const cleanToken = token ? token.replace(/"/g, '') : '';
      
      const response = await apiCall('/analytics/admin/system/stats/', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${cleanToken}`
        }
      });

      if (isStandardApiResponse(response) && response.status === 'success') {
        setVpsStats(response.data as VPSStats);
      } else {
        setError('VPS統計の取得に失敗しました');
      }
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.error('VPS統計取得エラー:', err);
      }
      setError(`データ取得エラー: ${err}`);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // 意図的に依存関係を空にして無限ループを防止

  useEffect(() => {
    fetchVPSStats();
  }, [fetchVPSStats]);

  const getRecommendationBadge = (recommendation: string) => {
    switch (recommendation) {
      case 'URGENT':
        return 'badge bg-danger';
      case 'RECOMMENDED':
        return 'badge bg-warning';
      case 'MONITOR':
        return 'badge bg-success';
      default:
        return 'badge bg-secondary';
    }
  };

  const getRecommendationText = (recommendation: string) => {
    switch (recommendation) {
      case 'URGENT':
        return '緊急スケールアップ';
      case 'RECOMMENDED':
        return 'スケールアップ推奨';
      case 'MONITOR':
        return '監視継続';
      default:
        return '不明';
    }
  };

  if (loading) {
    return (
      <div className="card">
        <div className="card-body text-center">
          <div className="spinner-border spinner-border-sm" role="status">
            <span className="visually-hidden">読み込み中...</span>
          </div>
          <p className="mt-2 mb-0 text-muted">VPS統計を取得中...</p>
        </div>
      </div>
    );
  }

  if (error || !vpsStats) {
    return (
      <div className="card">
        <div className="card-body">
          <div className="alert alert-warning mb-0">
            <i className="fas fa-exclamation-triangle me-2"></i>
            {error || 'VPS統計データがありません'}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="card-header d-flex justify-content-between align-items-center">
        <h5 className="mb-0">
          <i className="fas fa-server me-2"></i>
          VPS統計
        </h5>
        <button 
          className="btn btn-sm btn-outline-primary"
          onClick={fetchVPSStats}
          title="更新"
        >
          <i className="fas fa-sync-alt"></i>
        </button>
      </div>
      <div className="card-body">
        {/* VPS使用状況 */}
        <div className="mb-4">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <span className="fw-semibold">ストレージ使用量</span>
            <span className={getRecommendationBadge(vpsStats.vps_info.scale_recommendation)}>
              {getRecommendationText(vpsStats.vps_info.scale_recommendation)}
            </span>
          </div>
          <div className="progress mb-2" style={{ height: '20px' }}>
            <div 
              className={`progress-bar ${vpsStats.vps_info.usage_percentage > 80 ? 'bg-danger' : vpsStats.vps_info.usage_percentage > 60 ? 'bg-warning' : 'bg-success'}`}
              style={{ width: `${vpsStats.vps_info.usage_percentage}%` }}
            >
              {vpsStats.vps_info.usage_percentage}%
            </div>
          </div>
          <div className="d-flex justify-content-between text-sm text-muted">
            <span>{vpsStats.vps_info.total_storage_gb}GB 使用中</span>
            <span>容量: {vpsStats.vps_info.capacity_gb}GB</span>
          </div>
        </div>

        {/* ユーザー統計 */}
        <div className="row mb-4">
          <div className="col-4">
            <div className="text-center">
              <div className="h4 mb-0 text-primary">{vpsStats.user_stats.total_users}</div>
              <small className="text-muted">総ユーザー数</small>
            </div>
          </div>
          <div className="col-4">
            <div className="text-center">
              <div className="h4 mb-0 text-success">{vpsStats.user_stats.active_users}</div>
              <small className="text-muted">アクティブ</small>
            </div>
          </div>
          <div className="col-4">
            <div className="text-center">
              <div className="h4 mb-0 text-secondary">{vpsStats.user_stats.inactive_users}</div>
              <small className="text-muted">非アクティブ</small>
            </div>
          </div>
        </div>

        {/* サービス別使用量 */}
        <div className="mb-3">
          <h6 className="mb-2">サービス別使用量</h6>
          {vpsStats.service_breakdown.map((service, index) => (
            <div key={index} className="d-flex justify-content-between align-items-center mb-1">
              <span className="text-sm">{service.service__display_name}</span>
              <div className="text-end">
                <span className="badge bg-light text-dark me-2">{service.storage_gb}GB</span>
                <small className="text-muted">{service.user_count}ユーザー</small>
              </div>
            </div>
          ))}
        </div>

        {/* 成長トレンド */}
        <div className="border-top pt-3">
          <div className="d-flex justify-content-between">
            <span className="text-sm text-muted">30日間成長率</span>
            <span className={`fw-semibold ${vpsStats.trends.storage_growth_30d.startsWith('+') ? 'text-warning' : 'text-success'}`}>
              {vpsStats.trends.storage_growth_30d}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VPSStatsCard; 